import { Component, OnInit, AfterViewInit, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PlayerService } from '../../services/player.service';
import { Router } from '@angular/router';
import { Player } from '../../objects/player';
import { Group } from '../../objects/group';
import { GroupService } from '../../services/group.service';
import { SocialAuthService } from "@abacritt/angularx-social-login";
import { FacebookLoginProvider, GoogleLoginProvider } from "@abacritt/angularx-social-login";
import { SocialUser } from '@abacritt/angularx-social-login';
import { environment } from '../../../environments/environment';
import { CollectPhoneDialogComponent } from '../collect-phone-dialog/collect-phone-dialog.component';
import { normalizeUsPhone } from '../../shared/validators/phone.validator';
declare let pendo: any;
declare let AppleID: any;

@Component({
  selector: 's18-login-dialog',
  templateUrl: './login-dialog.component.html',
  styleUrls: ['./login-dialog.component.scss']
})
export class LoginDialogComponent implements OnInit {
  email: "";
  password: "";
  error: string;
  socialUser: SocialUser;
  sendingRequest = false;
  loginWithEmail: boolean = false;
  loginWithGoogle: boolean = false;
  loginWithFacebook: boolean = false;
  loginWithApple: boolean = false;

  private appleScriptLoaded = false;

  constructor(
    public dialog: MatDialog,
    private playerService: PlayerService,
    private groupService: GroupService,
    private router: Router,
    private authService: SocialAuthService,
    private dialogRef: MatDialogRef<LoginDialogComponent>,
    private ngZone: NgZone
  ) { }

  ngOnInit() {
    let player = this.playerService.getLoggedInPlayerInfo();
    if (player) {
      this.dialogRef.close(true);
      this.initializePendo(player);
      this.router.navigateByUrl("/dashboard");
      return;
    }

    // Subscribe to social auth state â€” fires when Google/Facebook sign-in completes
    this.authService.authState.subscribe((user) => {
      if (!user) return;
      this.socialUser = user;
      switch (user.provider) {
        case 'GOOGLE':
          this.loginWithGoogle = true;
          this.continueWithGoogle(user.idToken);
          break;
        case 'FACEBOOK':
          this.loginWithFacebook = true;
          // WI 58: Facebook does not reliably provide a phone, so collect one before creating the account.
          this.collectPhoneThen(user.email, (phone) => {
            this.playerService.registerWithEmail(user.email, user.firstName, user.lastName, "0", "", phone)
              .subscribe(
                resp => this.parseRegisterPlayerResponse(resp),
                error => {
                  this.error = "Unable to login, try again later";
                  this.loginWithFacebook = false;
                }
              );
          }, () => { this.loginWithFacebook = false; });
          break;
      }
    });

    this.loadAppleSignInScript();
  }

  parseRegisterPlayerResponse(resp) {
    this.sendingRequest = false;
    var ggkPlayer = JSON.parse(resp.d)[0];
    if (ggkPlayer) {
      this.playerService.setLoggedInPlayerInfo(ggkPlayer);
      this.playerService.loadAdminGroups(ggkPlayer.player_id.toString());
      this.initializePendo(ggkPlayer);
      this.dialogRef.close(true);
    }
  }

  initializePendo(player: Player) {
    console.log("pendo");
    pendo.initialize({
      apiKey: "2f187041-0658-4271-6b06-eaec9468385a",
      visitor: {
        id: player.player_id,
        email: player.email,
        full_name: player.first + ' ' + player.last
      },
      account: {
        id: player.stripe_customer_id // Highly recommended
      }
    });
  }

  onNoClick(): void {
    this.dialogRef.close(false);
  }

  signInWithGoogle(): void {
    // No-op: Google sign-in is now handled by the asl-google-signin-button directive
    // The authState subscription in ngOnInit handles the response
  }

  signInWithFB(): void {
    this.loginWithFacebook = true;
    this.authService.signIn(FacebookLoginProvider.PROVIDER_ID);
  }

  continueWithGoogle(token: string) {
    this.playerService.continueWithGoogle(token).subscribe(
      resp => {
        // WI 58: an existing phone-less account (or a new Google account) returns phone_required.
        var parsed = this.tryParsePhoneRequired(resp);
        if (parsed) {
          this.loginWithGoogle = false;
          this.collectPhoneThen(parsed.email, (phone) => {
            this.loginWithGoogle = true;
            this.playerService.finalizeGoogleRegistration(token, phone).subscribe(
              finalResp => this.parseRegisterPlayerResponse(finalResp),
              error => { this.error = "error"; this.loginWithGoogle = false; }
            );
          });
          return;
        }
        var ggkPlayer = JSON.parse(resp.d)[0];
        if (ggkPlayer) {
          this.playerService.setLoggedInPlayerInfo(ggkPlayer);
          this.playerService.loadAdminGroups(ggkPlayer.player_id.toString());
          this.initializePendo(ggkPlayer);
          this.dialogRef.close(true);
        }
      },
      error => {
        this.error = "error";
        this.loginWithGoogle = false;
      }
    );
  }

  // WI 58: returns { email } when the backend signals a phone is required, else null.
  private tryParsePhoneRequired(resp): { email: string } | null {
    try {
      const data = JSON.parse(resp.d);
      if (data && data.response === 'phone_required') {
        return { email: data.email };
      }
    } catch (e) {
      // normal login array — not a phone_required signal
    }
    return null;
  }

  // WI 58: open the shared phone dialog (email pre-filled), then run onPhone(normalizedPhone).
  private collectPhoneThen(email: string, onPhone: (phone: string) => void, onCancel?: () => void) {
    const ref = this.dialog.open(CollectPhoneDialogComponent, {
      disableClose: true,
      data: { email: email }
    });
    ref.afterClosed().subscribe((phone: string) => {
      if (phone) {
        onPhone(phone);
      } else if (onCancel) {
        onCancel();
      }
    });
  }

  // --- Apple Sign-In ---

  private loadAppleSignInScript(): void {
    if (this.appleScriptLoaded) return;
    if (document.getElementById('apple-signin-script')) {
      this.appleScriptLoaded = true;
      return;
    }

    const script = document.createElement('script');
    script.id = 'apple-signin-script';
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.onload = () => {
      this.appleScriptLoaded = true;
    };
    document.head.appendChild(script);
  }

  signInWithApple(): void {
    if (!this.appleScriptLoaded || typeof AppleID === 'undefined') {
      this.error = "Apple Sign-In is loading, please try again.";
      return;
    }

    this.loginWithApple = true;
    this.error = null;

    try {
      AppleID.auth.init({
        clientId: environment.APPLE_CLIENT_ID,
        scope: 'email name',
        redirectURI: window.location.origin,
        usePopup: true
      });
    } catch (e) {
      this.error = "Unable to initialize Apple Sign-In.";
      this.loginWithApple = false;
      return;
    }

    AppleID.auth.signIn().then(
      (response: any) => {
        this.ngZone.run(() => {
          const idToken = response?.authorization?.id_token;
          if (!idToken) {
            this.error = "Apple sign-in failed, please try again.";
            this.loginWithApple = false;
            return;
          }
          this.continueWithApple(idToken);
        });
      },
      (error: any) => {
        this.ngZone.run(() => {
          // User cancelled â€” Apple returns error with type 'user_cancelled_authorize'
          if (error?.error === 'user_cancelled_authorize' || error?.error === 'popup_closed_by_user') {
            this.loginWithApple = false;
            return; // No error, remain in dialog
          }
          this.error = "Apple sign-in failed, please try again.";
          this.loginWithApple = false;
        });
      }
    );
  }

  private continueWithApple(token: string): void {
    this.playerService.socialLogin('apple', token).subscribe(
      resp => {
        const parsed = JSON.parse(resp.d);
        // Check for error response from backend
        if (parsed?.response === 'error') {
          this.error = "Apple sign-in failed, please try again.";
          this.loginWithApple = false;
          return;
        }
        const ggkPlayer = Array.isArray(parsed) ? parsed[0] : parsed;
        if (ggkPlayer) {
          this.playerService.setLoggedInPlayerInfo(ggkPlayer);
          this.playerService.loadAdminGroups(ggkPlayer.player_id.toString());
          this.initializePendo(ggkPlayer);
          this.dialogRef.close(true);
        } else {
          this.error = "Apple sign-in failed, please try again.";
          this.loginWithApple = false;
        }
      },
      error => {
        this.error = "Apple sign-in failed, please try again.";
        this.loginWithApple = false;
      }
    );
  }

  // --- End Apple Sign-In ---

  onLogin(): void {
    if (!this.email || !this.password) {
      this.error = "Email and Password are required.";
      return;
    }
    this.sendingRequest = true;
    this.playerService.login(this.email, this.password).subscribe(
      response => {
        this.loadApp(response)
      },
      error => {
        this.error = 'error';
        this.sendingRequest = false;
      }
    )
  }

  loadApp(response: any): void {
    this.sendingRequest = false;
    var player = JSON.parse(response.d);
    this.playerService.setLoggedInPlayerInfo(player[0]);
    if (player[0]) {
      this.dialogRef.close(true);
      this.loadAdminGroups(player[0]);
      this.initializePendo(player[0]);
      return;
    }
    this.error = "Login attempt failed, please try again.";
  }

  loadAdminGroups(player: Player) {
    this.playerService.loadAdminGroups(player.player_id.toString());
    //this.router.navigateByUrl("dashboard/2");
  }
  forwardToCreate() {
    this.dialogRef.close(false);
    this.router.navigateByUrl("/register");
  }
}
